import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import axios from "axios";
import API_ENDPOINTS from "config/api";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function Almacenes() {
  const [almacenes, setAlmacenes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [almacenForm, setAlmacenForm] = useState({
    id: null,
    codigo: "",
    nombre: "",
    descripcion: "",
    ubicacion: "",
    responsable: "",
    telefono: "",
    email: "",
    capacidad_maxima: "",
    estatus: "activo",
  });

  const empresaId = localStorage.getItem("empresaId");

  useEffect(() => {
    fetchAlmacenes();
  }, [empresaId]);

  const fetchAlmacenes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ALMACENES, {
        params: { empresa_id: empresaId },
      });
      setAlmacenes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener almacenes:", error);
      setAlmacenes([]);
    }
  };

  const handleOpenDialog = (almacen = null) => {
    if (almacen) {
      setAlmacenForm(almacen);
    } else {
      setAlmacenForm({
        id: null,
        codigo: "",
        nombre: "",
        descripcion: "",
        ubicacion: "",
        responsable: "",
        telefono: "",
        email: "",
        capacidad_maxima: "",
        estatus: "activo",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAlmacenForm({ ...almacenForm, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      const currentEmpresaId = localStorage.getItem("empresaId") || 1;

      const data = {
        ...almacenForm,
        empresa_id: currentEmpresaId,
        capacidad_maxima: almacenForm.capacidad_maxima === "" ? null : parseInt(almacenForm.capacidad_maxima)
      };

      if (almacenForm.id) {
        await axios.put(API_ENDPOINTS.ALMACEN(almacenForm.id), data);
        alert("Almacén actualizado exitosamente");
      } else {
        await axios.post(API_ENDPOINTS.ALMACENES, data);
        alert("Almacén creado exitosamente");
      }
      fetchAlmacenes();
      handleCloseDialog();
    } catch (error) {
      console.error("Error al guardar almacén:", error);
      const errorDetail = error.response?.data?.errors
        ? JSON.stringify(error.response.data.errors)
        : (error.response?.data?.message || "");
      alert("Error al guardar almacén: " + (error.response?.data?.message || error.message) + "\n" + errorDetail);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro de eliminar este almacén?")) {
      try {
        await axios.delete(API_ENDPOINTS.ALMACEN(id));
        fetchAlmacenes();
      } catch (error) {
        console.error("Error al eliminar almacén:", error);
      }
    }
  };

  const columns = [
    { Header: "Código", accessor: "codigo", width: "10%", align: "left" },
    { Header: "Nombre", accessor: "nombre", align: "left" },
    { Header: "Ubicación", accessor: "ubicacion", width: "20%", align: "left" },
    { Header: "Responsable", accessor: "responsable", width: "15%", align: "left" },
    { Header: "Productos", accessor: "productos", width: "10%", align: "center" },
    { Header: "Estado", accessor: "estado", width: "10%", align: "center" },
    { Header: "Acciones", accessor: "acciones", width: "10%", align: "center" },
  ];

  const rows = almacenes.map((almacen) => ({
    codigo: almacen.codigo,
    nombre: almacen.nombre,
    ubicacion: almacen.ubicacion,
    responsable: almacen.responsable,
    productos: almacen.total_productos || 0,
    estado: (
      <Chip
        label={almacen.estatus === "activo" ? "Activo" : "Inactivo"}
        color={almacen.estatus === "activo" ? "success" : "default"}
        size="small"
      />
    ),
    acciones: (
      <MDBox display="flex" gap={1} justifyContent="center">
        <IconButton size="small" color="info" onClick={() => handleOpenDialog(almacen)}>
          <Icon>edit</Icon>
        </IconButton>
        <IconButton size="small" color="error" onClick={() => handleDelete(almacen.id)}>
          <Icon>delete</Icon>
        </IconButton>
      </MDBox>
    ),
  }));

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h6">Gestión de Almacenes</MDTypography>
        <MDButton
          variant="gradient"
          color="success"
          startIcon={<Icon>add</Icon>}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Almacén
        </MDButton>
      </MDBox>

      <Card>
        <MDBox>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </MDBox>
      </Card>

      {/* Dialog para crear/editar almacén */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{almacenForm.id ? "Editar Almacén" : "Nuevo Almacén"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código"
                name="codigo"
                value={almacenForm.codigo}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre"
                name="nombre"
                value={almacenForm.nombre}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="descripcion"
                value={almacenForm.descripcion}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ubicación"
                name="ubicacion"
                value={almacenForm.ubicacion}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Responsable"
                name="responsable"
                value={almacenForm.responsable}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono"
                name="telefono"
                value={almacenForm.telefono}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={almacenForm.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Capacidad Máxima"
                name="capacidad_maxima"
                type="number"
                value={almacenForm.capacidad_maxima}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {almacenForm.id ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
